import { assetsList } from 'abis/set/asset-icons'
import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import type { atomsWithQuery } from 'jotai-tanstack-query'
import { createPrices } from 'services/price-history'
import { assetConfig } from 'services/price-history/config'
import type { ChainAsset, Portfolios } from 'services/price-history/interfaces'
import {
  getPriceRangesStore,
  getPricesStore,
  timestampToDayIndex,
  writePriceRangesStore,
  writePricesStore,
} from 'services/price-history/utils'
import { type Nullable, assertMultiply, unixTimestampToDate } from 'utils'
import { type Address, getAddress, zeroAddress } from 'viem'
import { historicalAssetsStatsQuery } from '../daily-statistics'
import { getProductPortfolio, parseStorageToPortfolios } from '../daily-statistics/utils'

// INFO: Raw data response models
export type RawIndexProductResponse = {
  indexes: RawProductInfoDataResponse[]
}

export type SingleProductAssetAnatomy = {
  asset: string
  balance: string
  weight: string
}

export type RawProductAssetsAnatomyData = {
  asset: string
  balance: string
  index: string
  indexTimestamp: { assets: SingleProductAssetAnatomy[] }
  timestamp: string
  weight: string
}

export type RawProductAssetsAnatomyResponse = {
  historicalIndexAssets: RawProductAssetsAnatomyData[]
}

export type RawProductInfoDataResponse = {
  id: string
  name: string
  symbol: string
  totalSupply: string
  aumFee: string
  mintingFee: string
  redemptionFee: string
  totalFees: Nullable<string>
  creationDate: string
  k: Nullable<string>
  version: string
  assets?: RawAssetDataResponse[]
}

export type Asset = {
  chainID: string
  asset: string
  balance: string
  currencyID: Nullable<string>
  decimals: number
  name: string
  symbol: string
  weight: Nullable<string>
}

type RawAssetDataResponse = {
  chainID: string
  chainIndex: Nullable<string>
  latestSnapshot: Nullable<string>
  assets: Asset[]
}

// INFO: Parsed data models
export interface Product {
  address: Address
  chainId: NetworkType
  name: string
  symbol: string
  basePrice: Big
  basePriceETH: Big
  decimals: number
  marketCap: Big
  totalSupply: Big
  feeBurn: number
  feeMint: number
  feeAUMPercent: number
  created: Date
  assets?: Asset[]
}

export type ProductAssetsAnatomyData = {
  weight: number
  timestamp: number
  assets: SingleProductAssetAnatomy[]
}

export type AssetData = {
  id: string
  shares: Big
  weight: number
  decimals: number
  name: string
  symbol: string
  basePrice: Big
  vTokens: {
    apy: number
    id: string
    platformTotalSupply: Big
    totalAmount: Big
  }[]
}

export interface AssetDataWithPrices extends Asset {
  price: Big
}

export const defaultProduct: Product = {
  address: zeroAddress,
  chainId: NetworkType.Mainnet,
  name: '',
  symbol: '',
  basePrice: Big(1),
  basePriceETH: Big(1),
  decimals: 18,
  marketCap: Big(1),
  totalSupply: Big(1),
  feeBurn: 1,
  feeMint: 1,
  feeAUMPercent: 1,
  created: new Date(),
}

export type ProductReturn = ReturnType<typeof atomsWithQuery<Product>>

export type ProductRequest = ProductReturn['0']
export type ProductDispatch = ProductReturn['1']

export const parseProduct = async (
  response: RawProductInfoDataResponse,
  chainId: NetworkType,
  key?: string,
): Promise<Product> => {
  const productKey = `${chainId}:${response.id}`
  let productPortfolio = getProductPortfolio(productKey)

  let assetsPrices = getPricesStore()

  const dayIndex = timestampToDayIndex(assetConfig, new Date().setUTCHours(0, 0, 0, 0) / 1000)

  if (!assetsPrices[productKey] || !assetsPrices[productKey][dayIndex]) {
    let portfolio: Portfolios[] = []
    if (!productPortfolio) {
      portfolio = await historicalAssetsStatsQuery(
        key ?? '',
        chainId,
        response.id as Address,
        'parseProduct',
      )
    } else {
      portfolio = parseStorageToPortfolios(productPortfolio)
    }
    await createPrices(
      assetConfig,
      portfolio,
      getPricesStore,
      getPriceRangesStore,
      writePricesStore,
      writePriceRangesStore,
    )

    productPortfolio = getProductPortfolio(productKey)
    assetsPrices = await getPricesStore()
  }

  const intersection = productPortfolio?.history[productPortfolio?.history.length - 1]

  let intermediateCap = Big(0)
  if (intersection !== undefined) {
    for (const asset of intersection.assets) {
      const assetKey = `${asset.chainID}:${asset.asset}` as ChainAsset
      const assetPrice = assetsPrices[assetKey]
      if (assetPrice !== undefined) {
        const price = assetsPrices[assetKey][dayIndex]

        assertMultiply(intermediateCap.add(Big(asset.balance)), price)

        intermediateCap = intermediateCap.add(Big(asset.balance).mul(price))
      }
    }
  }
  const productPrice = assetsPrices?.[productKey]?.[dayIndex] ?? '1'
  const nativePrices = assetsPrices[`${chainId}:${zeroAddress}`]
  const nativePricesArray = Object.values(nativePrices ?? {}) ?? []
  const lastKnownNativePrice = nativePricesArray?.[nativePricesArray.length - 1] ?? '1'
  const nativePrice = nativePrices?.[dayIndex] ?? lastKnownNativePrice
  const result = {
    assets: response.assets
      ?.map((el) => el.assets)
      .flat()
      .map((el) => ({ ...el, symbol: assetsList[el.asset].symbol })),
    address: getAddress(response.id, chainId),
    chainId: chainId,
    name: response.name,
    symbol: response.symbol,
    basePriceETH: Big(nativePrice ?? '1'),
    decimals: 18,
    basePrice: Big(productPrice),
    marketCap: intermediateCap,
    totalSupply: Big(intersection?.totalSupply ?? 0),
    feeBurn: +response.redemptionFee,
    feeMint: +response.mintingFee,
    feeAUMPercent: +response.aumFee,
    created: unixTimestampToDate(response.creationDate),
  }
  return result
}
